import axios from 'axios';
import axiosRetry from 'axios-retry';
import { range } from 'lodash';
import moment from 'moment';
import pLimit from 'p-limit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCompleteUploadMutation,
  useCreateMultipartUploadMutation,
  useCreateSignedUploadMutation,
  useGetUploadSessionPartsLazyQuery,
} from 'types/atlas-graphql';
import { v4 as uuid } from 'uuid';

import { Button, Modal } from 'components/ui';
import { useAccountContext } from 'utils/account/AccountContext';
import { sendBrowserNotification, useBrowserNotifications } from 'utils/browserNotifications';
import { notifyBugsnag } from 'utils/bugsnag';

import { extractJpgFromCr2, isCr2File, updateIfCr2Files } from '../helpers';
import {
  type FileData,
  type ModalAction,
  type ModalActionArgs,
  type ModalUploadCompleteInput,
  type MultipartUploadHandler,
  type UploadFile,
} from '../types';
import { useUploadContext } from '../UploadContext';

import { ActionButtons, LeftButton, RightButton } from './FileUploadModal.style';
import { SelectedFileList } from './SelectedFileList';

const limit = pLimit(10);
const MULTIPART_PART_SIZE_BYTES = 64 * 1024 * 1024;
const MULTIPART_URL_BATCH_SIZE = 100;
axiosRetry(axios, {
  retries: 3,
  // Only make a retry attempt if it's not a cancellation error
  retryCondition: error => !axios.isCancel(error),
  onRetry: (retryCount, error, _requestConfig) =>
    console.log(
      `Upload request has failed ${retryCount} time(s). Request will be retried ${
        3 - retryCount
      } more time(s). ${error}`
    ),
});

export type OnCompleteStatus = 'CANCEL' | 'SUCCESS' | 'ERROR' | 'OK';

type MultipartUploadOperation = {
  cancelFailed?: boolean;
  cancelled: boolean;
  completed: boolean;
  failed: boolean;
};

const isMultipartUploadAlreadyComplete = (error: unknown) =>
  (error as { message?: unknown })?.message === 'Multipart attachment upload is already complete.';

export interface FileUploadModalProps {
  action: ModalAction[] | ((args: ModalActionArgs[]) => Promise<ModalAction[] | undefined>);
  fileInputRef: React.Ref<HTMLInputElement>;
  fileName: string;
  onComplete: (args: { status: OnCompleteStatus }) => void;
  onFileComplete: (args: ModalUploadCompleteInput) => Promise<unknown>;
}

export function FileUploadModal({
  fileInputRef,
  action,
  onComplete,
  onFileComplete,
  fileName,
}: FileUploadModalProps) {
  const { t } = useTranslation();
  const { notificationsEnabled, askPermission } = useBrowserNotifications();
  const {
    filteredFiles,
    corruptImgFiles,
    setCorruptImgFiles,
    modalProps,
    modalVisible,
    selectedFiles,
    setModalProps,
    setModalVisible,
    setSelectedFiles,
    setUploading,
    uploading,
    validFiles,
    setInitialSelection,
    showDragAndDropDirectoryError,
  } = useUploadContext();

  const displayAttachmentOptions = modalProps?.modalAttachmentProps?.displayAttachmentOptions;
  const attachmentRecordParentType = modalProps?.modalAttachmentProps?.attachmentRecordParentType;

  const source = useRef(axios.CancelToken.source());
  const activeMultipartUpload = useRef<{
    filename: string;
    handler: MultipartUploadHandler;
    operation: MultipartUploadOperation;
    uploadId: string;
  } | null>(null);
  const activeMultipartOperation = useRef<MultipartUploadOperation | null>(null);
  const cancellationFinalized = useRef(false);
  const isMounted = useRef(true);
  const wasCanceledUpload = useRef(false);
  const cancelRequest = (reason: string) => {
    if (source.current) {
      source.current.cancel(
        t(
          'inspection.uploadFailedContactSupport',
          'Upload failed. Please try again or contact support. Error message: {{reason}}',
          { reason }
        )
      );
    }
  };
  useEffect(() => {
    // Set and cancel the controller within a useEffect to match React lifecycle patterns
    isMounted.current = true;
    source.current = axios.CancelToken.source();
    return () => {
      isMounted.current = false;
      const operation = activeMultipartOperation.current;
      if (operation) {
        operation.cancelled = true;
      }
      const activeUpload = activeMultipartUpload.current;
      if (activeUpload) {
        void activeUpload.handler
          .abortUpload({
            filename: activeUpload.filename,
            uploadId: activeUpload.uploadId,
          })
          .then(() => {
            if (activeMultipartUpload.current === activeUpload) {
              activeMultipartUpload.current = null;
            }
          })
          .catch(error => {
            if (!isMultipartUploadAlreadyComplete(error)) {
              notifyBugsnag(`[Multipart upload abort error]: ${String(error)}`);
            }
          });
      }
      if (source.current) {
        cancelRequest('Component unmounted.');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { customer } = useAccountContext();
  const organizationId = customer?.id ?? null;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrorSummary, setShowErrorSummary] = useState<boolean>(false);
  const [createSignedUpload] = useCreateSignedUploadMutation();
  const [createMultipartUpload] = useCreateMultipartUploadMutation();
  const [completeUpload] = useCompleteUploadMutation();
  const [getUploadSessionParts, { data: uploadSessionPartsData }] =
    useGetUploadSessionPartsLazyQuery({
      fetchPolicy: 'no-cache',
    });

  function finishUploadCancellation() {
    if (!isMounted.current) {
      return;
    }
    if (cancellationFinalized.current) {
      return;
    }
    cancellationFinalized.current = true;
    onComplete && onComplete({ status: 'CANCEL' });
    setUploading(false);
    setSelectedFiles(previousSelectedFiles =>
      previousSelectedFiles.filter(
        ({ percent, status }) => percent !== 100 && status !== 'completed'
      )
    );

    // Need to manually reset the abort controller so that the user can try again if the modal doesn't auto-close on cancel
    source.current = axios.CancelToken.source();

    if (modalProps?.isMetadataUpload || modalProps?.isBulkUpload) {
      // clearing initial selection here on cancel.  If this is
      // not cleared, whenever a new multi-upload is attempted
      // the files from the previous cancelled upload are still
      // present in the selected file list.
      setInitialSelection([]);
      handleReset();
    }
  }

  const handleUploadCancel = useCallback(async () => {
    activeMultipartOperation.current && (activeMultipartOperation.current.cancelled = true);
    cancelRequest('Request was canceled.');
    if (activeMultipartUpload.current) {
      const activeUpload = activeMultipartUpload.current;
      try {
        await activeUpload.handler.abortUpload({
          filename: activeUpload.filename,
          uploadId: activeUpload.uploadId,
        });
      } catch (error: any) {
        if (isMultipartUploadAlreadyComplete(error)) {
          activeUpload.operation.completed = true;
          return;
        }
        activeUpload.operation.cancelFailed = true;
        setErrorMessage(error.message);
        return;
      }
      if (activeMultipartUpload.current === activeUpload) {
        activeMultipartUpload.current = null;
      }
    } else if (activeMultipartOperation.current) {
      // Session creation is still in flight. uploadMultipartFile will abort it once it has an ID,
      // then finish this cancellation only if that abort succeeds.
      return;
    }
    finishUploadCancellation();
    // eslint-disable-next-line
  }, [onComplete, setSelectedFiles, setUploading, modalProps]);

  useEffect(() => {
    if (modalProps?.uploadSession?.uploadId) {
      getUploadSessionParts({ variables: { uploadId: modalProps?.uploadSession?.uploadId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalProps?.uploadSession?.uploadId]);

  useEffect(() => {
    const canceledUpload = Boolean(modalProps?.canceledUpload);
    if (canceledUpload && !wasCanceledUpload.current) {
      void handleUploadCancel();
    }
    wasCanceledUpload.current = canceledUpload;
  }, [handleUploadCancel, modalProps?.canceledUpload]);

  function handleReset() {
    if (modalProps?.onClose) {
      modalProps?.onClose();
    }
    setModalVisible(false);
    if (typeof fileInputRef === 'object' && fileInputRef?.current) {
      // user might have left the route and continued async
      fileInputRef.current.value = '';
    }
    setSelectedFiles([]);
    setUploading(false);
    setCorruptImgFiles([]);
    setErrorMessage(null);
    setShowErrorSummary(false);
    setModalProps(null);
  }

  function handleCloseModal() {
    if (uploading) {
      setModalVisible(false);
    } else {
      handleReset();
    }
  }

  function removeSelectedFile(name: string) {
    setSelectedFiles(selectedFiles.filter(file => file.sourceName !== name));

    if (selectedFiles.length === 1) {
      // when the last file is being removed, close the modal
      handleCloseModal();
    }
  }

  async function handleFileUploadComplete({
    filename,
    path,
    category,
    description,
    attachmentCreated,
  }: ModalUploadCompleteInput) {
    setSelectedFiles(previousSelectedFiles =>
      previousSelectedFiles.map(f => (f.sourceName === path ? { ...f, status: 'completed' } : f))
    );
    await onFileComplete({
      filename,
      path,
      category,
      description,
      attachmentCreated,
    });
  }

  function handleFinishUpload({ status }: { status: OnCompleteStatus }) {
    if (onComplete) {
      onComplete({ status });
    }
    handleReset();
  }

  function handleCornisUploadFlag() {
    const filesToUpload = updateIfCr2Files(validFiles);
    setSelectedFiles(filesToUpload);

    return filesToUpload;
  }

  interface PartResponse {
    PartNumber: number;
    ETag: string;
  }

  interface MultipartPartResponse {
    etag: string;
    partNumber: number;
  }

  async function uploadMultipartFile(
    file: UploadFile,
    handler: MultipartUploadHandler,
    operation: MultipartUploadOperation
  ): Promise<boolean> {
    const fileData = file.data;
    if (!fileData) {
      throw new Error(
        t('attachments.fileDataUnavailable', 'File data is unavailable for {{filename}}.', {
          filename: file.name,
        })
      );
    }

    const cancelToken = source.current.token;
    if (operation.cancelled) {
      return false;
    }
    const { uploadId } = await handler.startUpload({
      filename: file.name,
      contentType: fileData.type || undefined,
    });
    const activeUpload = { filename: file.name, handler, operation, uploadId };
    activeMultipartUpload.current = activeUpload;
    if (operation.cancelled) {
      try {
        await handler.abortUpload({ filename: file.name, uploadId });
      } catch (error: any) {
        if (isMultipartUploadAlreadyComplete(error)) {
          operation.completed = true;
        } else {
          operation.cancelFailed = true;
          setErrorMessage(error.message);
          return false;
        }
      }
      if (activeMultipartUpload.current === activeUpload) {
        activeMultipartUpload.current = null;
      }
      if (operation.completed) {
        await handleFileUploadComplete({
          filename: file.name,
          path: file.sourceName,
          category: 'category' in file ? file.category : undefined,
          description: 'description' in file ? file.description : undefined,
          attachmentCreated: true,
        });
        return true;
      }
      return false;
    }

    let partSize = MULTIPART_PART_SIZE_BYTES;
    let partCount = Math.ceil(fileData.size / partSize);
    if (partCount > 10000) {
      partSize = Math.ceil(fileData.size / 10000);
      partCount = Math.ceil(fileData.size / partSize);
    }

    const parts: MultipartPartResponse[] = [];
    setSelectedFiles(previousSelectedFiles =>
      previousSelectedFiles.map(selectedFile =>
        selectedFile.sourceName === file.sourceName
          ? { ...selectedFile, status: 'uploading' }
          : selectedFile
      )
    );

    for (
      let startPartNumber = 1;
      startPartNumber <= partCount;
      startPartNumber += MULTIPART_URL_BATCH_SIZE
    ) {
      if (operation.cancelled) {
        return false;
      }
      const partNumbers = range(
        startPartNumber,
        Math.min(startPartNumber + MULTIPART_URL_BATCH_SIZE, partCount + 1)
      );
      const partUrls = await handler.getPartUrls({
        filename: file.name,
        partNumbers,
        uploadId,
      });
      if (operation.cancelled) {
        return false;
      }

      await Promise.all(
        partUrls.map(({ partNumber, url }) =>
          limit(async () => {
            if (operation.cancelled) {
              return;
            }
            const chunk = fileData.slice(
              (partNumber - 1) * partSize,
              Math.min(partNumber * partSize, fileData.size)
            );
            try {
              const response = await axios.put(url, chunk, {
                cancelToken,
              });
              if (operation.cancelled) {
                return;
              }
              const etag = String(response.headers?.etag ?? '').trim();
              if (!etag) {
                throw new Error(
                  t(
                    'attachments.missingUploadPartEtag',
                    'Upload part {{partNumber}} was not confirmed.',
                    { partNumber }
                  )
                );
              }
              parts.push({ etag, partNumber });
              setSelectedFiles(previousSelectedFiles =>
                previousSelectedFiles.map(selectedFile =>
                  selectedFile.sourceName === file.sourceName
                    ? {
                        ...selectedFile,
                        percent: Math.round((parts.length / partCount) * 100),
                      }
                    : selectedFile
                )
              );
            } catch (error) {
              if (!operation.cancelled) {
                operation.failed = true;
                operation.cancelled = true;
                cancelRequest('Multipart upload part failed.');
              }
              throw error;
            }
          })
        )
      );

      if (operation.cancelled) {
        return false;
      }
    }

    await handler.completeUpload({
      filename: file.name,
      uploadId,
      parts: [...parts].sort((a, b) => a.partNumber - b.partNumber),
      category: 'category' in file ? file.category : undefined,
      description: 'description' in file ? file.description : undefined,
    });
    if (operation.cancelled && !operation.completed) {
      return false;
    }
    if (activeMultipartUpload.current?.operation === operation) {
      activeMultipartUpload.current = null;
    }
    await handleFileUploadComplete({
      filename: file.name,
      path: file.sourceName,
      category: 'category' in file ? file.category : undefined,
      description: 'description' in file ? file.description : undefined,
      attachmentCreated: true,
    });
    return true;
  }

  async function handleUpload() {
    setErrorMessage(null);
    setUploading(true);
    cancellationFinalized.current = false;

    let err;
    let multipartCompletionWon = false;
    // Flag that is set when an upload is cancelled.
    let cancelled: boolean = false;

    const filesToUpload = handleCornisUploadFlag();
    const multipartMinimumFileSizeBytes = modalProps?.multipartMinimumFileSizeBytes ?? 0;
    const multipartFiles = modalProps?.multipartUpload
      ? filesToUpload.filter(file => (file.data?.size ?? 0) >= multipartMinimumFileSizeBytes)
      : [];
    const standardFiles = modalProps?.multipartUpload
      ? filesToUpload.filter(file => !multipartFiles.includes(file))
      : filesToUpload;
    const fileNames: ModalActionArgs[] = standardFiles.map(file => ({ fileName: file.sourceName }));

    if (modalProps?.multipartUpload && multipartFiles.length > 0) {
      const operation: MultipartUploadOperation = {
        cancelled: false,
        completed: false,
        failed: false,
      };
      activeMultipartOperation.current = operation;
      for (const file of multipartFiles) {
        try {
          const completed = await uploadMultipartFile(file, modalProps.multipartUpload, operation);
          if (!completed) {
            if (operation.cancelled && !operation.cancelFailed) {
              finishUploadCancellation();
            }
            if (activeMultipartOperation.current === operation) {
              activeMultipartOperation.current = null;
            }
            return;
          }
        } catch (error: any) {
          if (
            !operation.failed &&
            !operation.completed &&
            (operation.cancelled || axios.isCancel(error))
          ) {
            if (operation.cancelled && !operation.cancelFailed) {
              finishUploadCancellation();
            }
            if (activeMultipartOperation.current === operation) {
              activeMultipartOperation.current = null;
            }
            return;
          }
          err = true;
          const activeUpload = activeMultipartUpload.current;
          if (activeUpload?.operation === operation) {
            try {
              await activeUpload.handler.abortUpload({
                filename: activeUpload.filename,
                uploadId: activeUpload.uploadId,
              });
            } catch (abortError: any) {
              notifyBugsnag(`[Multipart upload abort error]: ${String(abortError)}`);
            }
            activeMultipartUpload.current = null;
          }
          setErrorMessage(error.message);
          setSelectedFiles(previousSelectedFiles =>
            previousSelectedFiles.map(selectedFile =>
              selectedFile.sourceName === file.sourceName
                ? { ...selectedFile, status: 'error' }
                : selectedFile
            )
          );
          break;
        }
      }
      if (activeMultipartOperation.current === operation) {
        activeMultipartOperation.current = null;
      }
      multipartCompletionWon = operation.cancelled && operation.completed;
    }

    if (!err && modalProps?.multipartProps) {
      const { recordId: multiPartRecordId, outputDataType } = modalProps.multipartProps;
      // This logic is assuming we are only uploading a single large file
      // to be uploaded using the multi-part upload process.  If we need
      // to scale this out to include bulk multi-part uploads, we will
      // need to make some changes.
      const uploadSession = modalProps.uploadSession ?? null;
      const mainFile = filesToUpload[0].data;
      const recordId = multiPartRecordId ? multiPartRecordId : uploadSession?.recordId ?? uuid();
      const bundleId = uploadSession?.bundleId ?? moment().format('x');

      if (!mainFile) {
        err = true;
        if (onComplete) {
          onComplete({ status: 'ERROR' });
        }
      }

      if (mainFile) {
        setUploading(true);
        const input = {
          recordId: recordId,
          dataType: outputDataType,
          organizationId,
          bundleId,
          files: [{ fileName: filesToUpload[0].sourceName }],
          params: { draftId: recordId },
        };

        let sessionUploadId = uploadSession?.uploadId;
        const uploadedParts: Array<{ eTag: string; partNumber: number }> = [];
        if (sessionUploadId) {
          if (uploadSessionPartsData?.uploadSessionByUploadId?.parts) {
            uploadedParts.push(...uploadSessionPartsData?.uploadSessionByUploadId?.parts);
          }
        } else {
          const { data: createMultipartUploadData } = await createMultipartUpload({
            variables: { input },
          });
          const { UploadId: uploadId } = createMultipartUploadData?.createMultipartUpload[0] || {};
          sessionUploadId = uploadId;
        }

        const transformSessionParts = uploadedParts.map((part: any) => {
          return {
            ETag: part.eTag,
            PartNumber: part.partNumber,
          };
        });

        const completeInput = {
          ...input,
          uploadId: sessionUploadId,
          parts: {
            Parts: (transformSessionParts as PartResponse[]) ?? [],
          },
        };

        let chunkSize = 5242880;
        let numberOfParts = Math.ceil(mainFile.size / chunkSize);

        // AWS multipart uploads have a limit of 10000 parts.
        // If we are above that, recalc size and parts
        if (numberOfParts > 10000) {
          chunkSize = Math.ceil(mainFile.size / 10000);
          numberOfParts = Math.ceil(mainFile.size / chunkSize);
        }
        const sessionParts = uploadedParts.map((part: any) => part.partNumber) || [];

        // Set the file to "uploading" so the user has some visual indication
        // that work is being done, even if we aren't technically "uploading"
        // any of the parts yet.
        setSelectedFiles(previousSelectedFiles =>
          previousSelectedFiles.map(f =>
            f.sourceName === filesToUpload[0].sourceName ? { ...f, status: 'uploading' } : f
          )
        );

        // Because our upload sessions are happening concurrently, we cannot be sure when resuming an upload
        // that there is a correct "last part" that has been uploaded
        // (e.g. just because the greatest part number is 5 doesn't mean that 1-4 have completed)
        // Here we populate a "missingParts" array with every UploadSession part that has not yet completed
        const missingPartNumbers = range(1, numberOfParts + 1).filter(
          n => sessionParts.indexOf(n) === -1
        );
        const signedUploadInput = {
          recordId: recordId,
          dataType: outputDataType,
          files: missingPartNumbers.map(mpn => ({
            fileName: filesToUpload[0].sourceName,
            partNumber: mpn,
          })),
          uploadId: sessionUploadId,
        };
        const missingParts: Array<any> = [];
        const { data: createSignedUploadData } = await createSignedUpload({
          variables: { input: signedUploadInput },
        });
        if (createSignedUploadData?.createSignedUpload) {
          const uploadParts = createSignedUploadData?.createSignedUpload.map(su => ({
            url: su?.url,
            partNumber: su?.partNumber,
          }));
          missingParts.push(...uploadParts);
        }

        await Promise.all(
          missingParts.map(
            async ({ partNumber, url }: { partNumber: number; url: string }, index: number) => {
              const partEnd = Math.min(partNumber * chunkSize, mainFile.size);
              const chunk = mainFile.slice(partNumber * chunkSize - chunkSize, partEnd);
              await limit(async () => {
                try {
                  // If the cancelled flag is set, don't attempt to perform
                  // any more uploads.  If this is not here, all subsequent uploads
                  // will be attempted and immediately fail due to the cancel token being
                  // set.
                  if (cancelled) {
                    return;
                  }
                  if (url) {
                    await axios
                      .put(url, chunk, { cancelToken: source.current.token })
                      .then(response => {
                        setSelectedFiles(previousSelectedFiles =>
                          previousSelectedFiles.map(f => {
                            // Our completed percentage is the total number of file chunks (ex. 20)
                            // minus the number of missing values (ex. 5) plus the index of the current value
                            // divided by the number of file chunks (ex. ((20 - 5 + 0) / 20) * 100 = 75% complete)
                            const currentChunkPercentCalc = Math.round(
                              ((numberOfParts - missingParts.length + index) / numberOfParts) * 100
                            );
                            return f.sourceName === filesToUpload[0].sourceName
                              ? {
                                  ...f,
                                  // Because the chunks can upload out of order, we only want to show forward progress.
                                  percent:
                                    f.percent > currentChunkPercentCalc
                                      ? f.percent
                                      : currentChunkPercentCalc,
                                }
                              : f;
                          })
                        );
                        completeInput.parts.Parts.push({
                          ETag: String(response.headers.etag ?? ''),
                          PartNumber: partNumber,
                        });
                        return;
                      });
                  }
                } catch (error: any) {
                  const message = `[File Upload error]: ${JSON.stringify(error)}`;
                  notifyBugsnag(message);
                  setUploading(false);
                  setErrorMessage(error.message);
                  if (axios.isCancel(error)) {
                    // set cancelled flag to prevent more upload attempts
                    cancelled = true;
                    throw error.message;
                  } else {
                    setSelectedFiles(
                      selectedFiles.map(f =>
                        f.sourceName === filesToUpload[0].sourceName ? { ...f, status: 'error' } : f
                      )
                    );
                  }
                }
              });
            }
          )
        ).then(async () => {
          completeInput.parts.Parts.sort(
            (a: PartResponse, b: PartResponse) => a.PartNumber - b.PartNumber
          );
          setSelectedFiles(previousSelectedFiles =>
            previousSelectedFiles.map(f =>
              f.sourceName === filesToUpload[0].sourceName ? { ...f, status: 'completed' } : f
            )
          );
          await completeUpload({ variables: { input: completeInput } });
        });
      }
    } else if (!err && !multipartCompletionWon && standardFiles.length > 0) {
      const urls = typeof action === 'function' ? await action(fileNames) : action;

      if (!urls) {
        // if URLs are undefined, prevent upload.
        setErrorMessage(
          t(
            'components.unableToUploadRetry',
            'Unable to upload at this time. Press Upload to retry.'
          )
        );
        setUploading(false);
        err = true;
        if (onComplete) {
          onComplete({ status: 'ERROR' });
        }
        return;
      }

      const fileUploads = standardFiles.map((file, index) =>
        limit(async () => {
          // If the cancelled flag is set, don't attempt to perform
          // any more uploads.  If this is not here, all subsequent uploads
          // will be attempted and immediately fail due to the cancel token being
          // set.
          if (cancelled) {
            return;
          }
          // FIXME: this check will be gone when we make attachments more like inspection uploads
          const currentFile = urls.length > 1 ? index : 0;
          const isCr2 = isCr2File(file);

          const { url, fields = {} } = urls[currentFile] || {};

          const formData: FormData = new FormData();

          Object.keys(fields).forEach(key => {
            const value = fields[key];
            formData.append(key, value);
          });

          const fileData: File | FileData = isCr2
            ? await extractJpgFromCr2(file.data as File)
            : (file.data as FileData);
          formData.append('file', fileData as File);

          try {
            setSelectedFiles(previousSelectedFiles =>
              previousSelectedFiles.map(f =>
                f.sourceName === file.sourceName ? { ...f, status: 'uploading' } : f
              )
            );
            await axios.post(url, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              cancelToken: source.current.token,
            });

            if (displayAttachmentOptions) {
              const category: string | undefined =
                'category' in file ? (file.category as string) : undefined;
              const description: string | undefined =
                'description' in file ? (file.description as string) : undefined;
              await handleFileUploadComplete({
                filename: file.name,
                path: file.sourceName,
                category: category,
                description: description,
              });
            } else {
              await handleFileUploadComplete({ filename: file.name, path: file.sourceName });
            }
          } catch (error: any) {
            const message = `[File Upload error]: ${JSON.stringify(error)}`;
            notifyBugsnag(message);
            if (axios.isCancel(error)) {
              // set cancelled flag to prevent more upload attempts
              cancelled = true;
              setUploading(false);
              setErrorMessage(error.message ?? null);
              throw error.message;
            } else {
              err = true;

              setSelectedFiles(previousSelectedFiles =>
                previousSelectedFiles.map(f =>
                  f.sourceName === file.sourceName ? { ...f, status: 'error' } : f
                )
              );
            }
          }
        })
      );

      await Promise.all(fileUploads);
    }

    if (err) {
      setShowErrorSummary(true);
      handleFinishUpload({ status: 'ERROR' });
    } else {
      sendBrowserNotification({
        notificationsEnabled,
        subject: t('components.fileUpload', 'File upload'),
        message: t('components.fileUploadComplete', 'Your file upload is complete.'),
      });

      if (modalProps?.isBulkUpload && validFiles.length === 0) {
        // If this is a bulk upload, and we have 0 valid files, we still want to send the complete event
        // because the details metadata file could contain Image URL's.  In which case, there will be no
        // images uploaded, but they will be downloaded in the pipeline
        const detailsFile = selectedFiles.find(f => f.name.toLocaleLowerCase().includes('details'));
        handleFileUploadComplete({
          filename: detailsFile?.name ?? '',
          path: detailsFile?.path ?? '',
        });
      }

      handleFinishUpload({ status: 'SUCCESS' });
    }
  }

  useEffect(
    () => {
      if (modalProps?.isMetadataUpload) {
        handleUpload();
      }
    },
    [modalProps] // eslint-disable-line
  );

  const uploadDisabled = () => {
    return !modalProps?.isBulkUpload && !validFiles.length;
  };

  return (
    <Modal
      title={t('inspection.uploadFiles', 'Upload Files')}
      open={modalVisible}
      onCancel={handleCloseModal}
      styles={{ body: { padding: '1rem 1.5rem' } }}
      destroyOnHidden={true}
      footer={
        uploading ? (
          <Button onClick={handleUploadCancel}>{t('common.cancel', 'Cancel')}</Button>
        ) : !showErrorSummary ? (
          <ActionButtons>
            {typeof notificationsEnabled === 'undefined' && (
              <LeftButton onClick={askPermission} className="left-btn">
                {t('components.browserNotifications', 'Browser Notifications')}
              </LeftButton>
            )}
            <Button onClick={handleReset}>{t('common.cancel', 'Cancel')}</Button>
            <RightButton
              disabled={uploadDisabled()}
              data-testid="upload-modal-submit-button"
              htmlType="submit"
              onClick={handleUpload}
              type="primary"
            >
              {t('common.upload', 'Upload')}
            </RightButton>
          </ActionButtons>
        ) : (
          <Button onClick={() => handleFinishUpload({ status: 'OK' })}>
            {t('settings.ok', 'Ok')}
          </Button>
        )
      }
    >
      <SelectedFileList
        errorMessage={errorMessage}
        fileName={fileName}
        filteredFiles={filteredFiles}
        corruptFiles={corruptImgFiles}
        onRemove={removeSelectedFile}
        showErrorSummary={showErrorSummary}
        uploading={uploading}
        validFiles={validFiles}
        displayAttachmentOptions={displayAttachmentOptions}
        attachmentRecordParentType={attachmentRecordParentType}
        isBulkUpload={modalProps?.isBulkUpload}
        showDragAndDropDirectoryError={showDragAndDropDirectoryError}
      />
    </Modal>
  );
}
